import cl from 'classnames';
import * as React from 'react';
import styled from 'styled-components';

import { ButtonClose } from '../ButtonClose/ButtonClose';
import { figmaColors } from '../colors';
import { colorsV2 } from '../colors-v2';
import { Typography } from '../DesignSystemV2/Typography';
import { QuestionV2 } from '../Question/QuestionV2';
import { SubText } from '../Typography';

export const QuestionOptionType = {
  QUESTION_OPTION_NOT_SELECTED: 'question-option-not-selected',
  QUESTION_OPTION_SELECTED: 'question-option-selected',
} as const;

export type QuestionOptionType = (typeof QuestionOptionType)[keyof typeof QuestionOptionType];

interface QuestionOptionProps extends React.HTMLAttributes<HTMLDivElement> {
  displayMode: QuestionOptionType;
  shape1?: string;
  shape2?: string;
  shape1Text?: string | number;
  shape2Text?: string | number;
  text: string;
  selected?: boolean;
  onRemoveSelectedOption?: (idx: any) => void;
}

export const QuestionOption = (props: QuestionOptionProps) => {
  const {
    displayMode,
    shape1,
    shape2,
    shape1Text,
    shape2Text,
    text,
    selected,
    onRemoveSelectedOption,
    ...rest
  } = props;

  return (
    <Main className="circle-text" {...rest}>
      <QuestionContainer
        className={cl('question-circle-text-container', displayMode, {
          selected,
          [`shape-${shape1}`]: true,
        })}
      >
        {shape1 && <QuestionV2 no={shape1Text} />}

        {shape2 && (
          <Shape2
            className={cl('question-circle', displayMode, { selected, [`shape-${shape2}`]: true })}
          >
            <SubText style={{ color: colorsV2.white100 }}>{shape2Text}</SubText>
          </Shape2>
        )}

        <QuestionText className={cl('question-text', displayMode, { selected })}>
          <Typography.Text variant="medium/14">{text}</Typography.Text>
        </QuestionText>
        {selected && <ButtonClose variant="transparent" onClick={onRemoveSelectedOption} />}
      </QuestionContainer>
    </Main>
  );
};

const Main = styled.section`
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;
  width: 100%;
  border-radius: 8px;
  .question-circle {
    margin-right: 8px;
  }

  &.question-option-wrapper {
    &:hover {
      background: ${figmaColors.bg5};
      .question-circle.question-option {
        background: ${figmaColors.W2B1};
      }
    }
    &.selected:hover {
      background: ${figmaColors.bg7};
      .question-circle.question-option {
        background: ${figmaColors.blue6};
      }
    }
  }
`;

const QuestionContainer = styled.section`
  flex: 1;
  display: flex;
  align-items: center;

  > div > span {
    font-size: 10px;
    font-weight: 600;
    line-height: 16px;
    color: ${colorsV2.white100};
  }
  .shape-rounded-rectangle {
    border-radius: 8px;
  }
`;

export const Shape2 = styled.div`
  border-radius: 50%;
  min-width: 24px;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;

  &.${QuestionOptionType.QUESTION_OPTION_NOT_SELECTED} {
    background: ${colorsV2.gray10};
    border: 1px dashed ${colorsV2.gray60};
    > span {
      color: ${colorsV2.gray80};
    }
  }
  &.${QuestionOptionType.QUESTION_OPTION_SELECTED} {
    background: ${colorsV2.blue100};
    border: 1px solid ${colorsV2.blue100};
  }
`;

export const QuestionText = styled.div`
  font-weight: 500;
  display: flex;
  flex: 1;

  .reading-text {
    line-height: 20px;
    white-space: nowrap;
  }
  &.${QuestionOptionType.QUESTION_OPTION_NOT_SELECTED} {
    > span {
      color: ${colorsV2.gray80};
    }
  }
  &.${QuestionOptionType.QUESTION_OPTION_SELECTED} {
    > span {
      color: ${colorsV2.black100};
    }
  }
`;
